import { Button, Loader, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { TruckIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";

import { Plate } from "@/components/@custom/Plate";
import { useSession } from "@/hooks/@custom/useSession";
import { useGetQueueDriverPositionQuery, useSaveCheckinMutation } from "@/store/apis/nextcargo";

interface CheckInStepConfirmProps {
  onNextStep: () => void;
}

export function CheckInStepConfirm({ onNextStep }: CheckInStepConfirmProps) {
  const { colors } = useMantineTheme();
  const navigate = useNavigate();

  const { driver } = useSession();
  const driverId = driver?.id;
  const driverCompanyId = driver?.company.id;
  const [driverFirstName] = driver?.name.split(" ") || [""];

  const [saveCheckinTrigger, { isLoading: isLoadingCheckin }] = useSaveCheckinMutation();
  const { data: driverPositionData, isLoading: isLoadingDriverPosition } = useGetQueueDriverPositionQuery({ id: driverId! }, { skip: !driverId });

  const licensePlate = driverPositionData?.queue.truck.licensePlate;

  async function handleCheckIn() {
    try {
      if (!driverPositionData) throw new Error("Você não tem posição na fila");

      const data = {
        id: null,
        isPresent: true,
        status: true,
        queue: {
          id: driverPositionData.queue.id
        },
        company: {
          id: driverCompanyId!
        }
      };

      await saveCheckinTrigger(data);
      onNextStep();
    } catch {
      // Do nothing
    }
  }

  return (
    <>
      {isLoadingDriverPosition && (
        <Stack align="center" justify="center" mih="inherit">
          <Loader size="xl" />
        </Stack>
      )}

      {!isLoadingDriverPosition && (
        <Stack gap="md">
          <Title order={1}>{driverFirstName}</Title>

          <Stack className="text-center" gap="sm">
            <Text>Ao realizar o check-in você confirma estar ciente e utilizando todos os EPIs necessários para acessar nossa indústria.</Text>

            <Text fw="bold" size="lg" lh="normal">
              Capacete, máscara, óculos, protetor auricular, camisa, calças e botas.
            </Text>

            <Text ta="justify">
              Da mesma forma, o veículo abaixo está completamente livre de contaminação de outros materiais e apto para realizar carregamento e transporte do nosso produto.
            </Text>

            {licensePlate && (
              <Stack align="center" justify="center" gap={6}>
                <TruckIcon size={64} color={colors.blue[6]} />
                <Plate color="text-primary" plate={licensePlate} />
              </Stack>
            )}
          </Stack>

          <Stack className="text-center" gap="md">
            <Text size="xl" c="red.6" fw="bold">
              Deseja realizar o check-in?
            </Text>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              <Button onClick={() => navigate({ to: "/app" })} variant="outline" disabled={isLoadingCheckin}>
                Não
              </Button>

              <Button onClick={handleCheckIn} loading={isLoadingCheckin}>
                Sim
              </Button>
            </div>
          </Stack>
        </Stack>
      )}
    </>
  );
}
